// New-learner creation — the reader's card (HOP-22).
//
// A full-screen modal takeover: "Add a learner" from the learner switcher,
// and the first-run prompt when the account has no learners yet (the parent
// is in a focused flow, no app chrome).
//
// The form IS a printed object — a library reader's card — so it earns ink
// keylines + misregistration (Law 1). Name is written by hand (Caveat: the
// human voice); age is a row of rubber-stamp boxes (selected = ink-stamped,
// Law 4). The CTA stays flat unprinted paper until the card is filled in
// (Law 2), then becomes the raised coral letterpress block whose label
// names the payoff. Time honesty: the "about 3 min" postmark; NO step
// counters.
//
// Design source: specs/Hopscotch-UI-handoff/New Learner Empty.html +
// New Learner Filled.html (screens/learner-setup.jsx).
//
// State + handlers live in useCreateLearnerModal / useReaderCardForm
// (src/hooks) — this file is the presentation layer only. Styling lives
// in styles/create-learner-modal.css.

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { useCreateLearnerModal } from '@/hooks/useCreateLearnerModal'
import { useGlobalState } from '@/stores/globalState'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { AGE_STAMPS, useReaderCardForm } from '@/hooks/useReaderCardForm'
import { Grain } from '@/components/hs/Grain'
import { WelcomeCourt } from '@/components/hs/WelcomeCourt'
import '@/styles/create-learner-modal.css'
import { MaskedName } from '@/lib/privacyMask'

// HOP-135 (P0, COPPA): parental-consent gate. Ashish confirmed (Slack) a
// single affirmation is enough for now — "I am the parent/guardian" + "I
// agree to Terms" combined into one checkbox, not two, and no stronger
// mechanism (e.g. re-auth) required yet. This has to block BEFORE any child
// data entry, not just before submit — so it's a separate screen the modal
// renders instead of ReaderCardSpread until consented, not a checkbox bolted
// onto the existing form (the name/age inputs never mount until this passes).

function AgeStamp({ n, selected, onClick }: { n: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn('create-learner-modal__age-stamp', selected && 'create-learner-modal__age-stamp--selected')}
    >
      {n}
    </button>
  )
}

// The card itself — heading, the printed reader's card, the CTA. Owns its
// own form state, so each mount starts blank. `onCreated` fires after a
// successful save (the modal closes itself with it; the surface needs
// nothing — setting the active learner routes it away).
function ReaderCardSpread({ onCreated }: { onCreated?: () => void }) {
  const { name, setName, ageStamp, setAgeStamp, error, ready, kid, isPending, handleSubmit } = useReaderCardForm(onCreated)

  return (
    <div className="create-learner-modal__spread">
      {/* left — the words */}
      <div className="create-learner-modal__words">
        <h1 className="create-learner-modal__heading">
          <div>Tell us about</div>
          <div>
            your{' '}
            <span className="create-learner-modal__highlight">
              <span aria-hidden="true" className="create-learner-modal__highlight-mark" />
              <span className="create-learner-modal__highlight-text">little</span>
            </span>{' '}
            <span className="create-learner-modal__accent-coral">reader.</span>
          </div>
        </h1>
        <p className="create-learner-modal__intro-copy">
          A 3-minute placement helps us tune every game and practice to exactly where they are
          right now (not where a label says they should be).
        </p>
      </div>

      {/* right — the reader's card (printed object: keeps its ink) */}
      <div className="create-learner-modal__card-col">
        <div className="create-learner-modal__card-wrap">
          {/* shelf shadow + misregistered back pass */}
          <div className="create-learner-modal__card-shadow" />
          <div className="create-learner-modal__card-back" />
          <div className="create-learner-modal__card">
            {/* postmark — time honesty at the point of commitment */}
            <div className="create-learner-modal__postmark">
              <div className="create-learner-modal__postmark-ring" />
              <div className="create-learner-modal__postmark-time">
                3<span className="create-learner-modal__postmark-time-unit">&thinsp;min</span>
              </div>
              <div className="create-learner-modal__postmark-about">about</div>
            </div>

            {/* card header */}
            <div className="create-learner-modal__card-header">
              <div className="create-learner-modal__card-eyebrow">New reader</div>
              <div className="create-learner-modal__card-rule" />
            </div>

            {/* name — written by hand on the ruled line */}
            <div className="create-learner-modal__field">
              <div className="create-learner-modal__field-label">Their name</div>
              <div className={cn('create-learner-modal__name-line', name.trim() && 'create-learner-modal__name-line--filled')}>
                <input
                  className="create-learner-modal__name-input"
                  type="text"
                  value={name} // ph-safe: input value, maskAllInputs covers it
                  placeholder="write it here"
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit()
                  }}
                  aria-label="Learner's name"
                />
              </div>
            </div>

            {/* age — rubber-stamp boxes */}
            <div>
              <div className="create-learner-modal__field-label create-learner-modal__field-label--age">How old are they?</div>
              <div className="create-learner-modal__age-row">
                {AGE_STAMPS.map((n) => (
                  <AgeStamp key={n} n={n} selected={ageStamp === n} onClick={() => setAgeStamp(n)} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {error && <p className="create-learner-modal__error">{error}</p>}

        {/* CTA — flat paper until the card is filled in; raised
            letterpress (= tappable) once name + age are set */}
        <button
          type="button"
          disabled={!ready || isPending}
          onClick={handleSubmit}
          className="hs-press create-learner-modal__cta"
        >
          {isPending ? 'Getting ready…' : 'Start the placement'}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 10h13M11 4l6 6-6 6" />
          </svg>
        </button>
        <p className="create-learner-modal__cta-helper">
          Three quick questions for you first, then it&rsquo;s <MaskedName>{kid}</MaskedName>&rsquo;s turn (best played
          together).
        </p>
      </div>
    </div>
  )
}

// First-run brand moment (HOP-36, optional polish): the account has no
// learner yet, so this is the first thing the parent sees right after
// signup/verification — a one-time banner (shares the WelcomeCourt motif
// with the Sign Up screen, HOP-33) so the reader's card doesn't feel like a
// bare form dropped in front of them. Naturally shows only once: it's gated
// on there being zero learners, which stops being true the moment the first
// one is created.
function FirstRunBanner() {
  return (
    <div className="create-learner-modal__first-run">
      <div className="create-learner-modal__first-run-court">
        <WelcomeCourt maxWidth={88} />
      </div>
      <p className="create-learner-modal__first-run-quote">
        Reading they&rsquo;ll actually want to do.
      </p>
    </div>
  )
}

// HOP-135 (P0, COPPA): affirmative parental consent, required before the
// reader's card (name + age) ever mounts. Plain <a> with target="_blank"
// (not react-router <Link>) so opening the Terms doesn't navigate the SPA
// away from this full-screen takeover — same pattern CreateAccountPage uses
// for its own Terms/Privacy links.
function ParentalConsentGate({ onConsent }: { onConsent: () => void }) {
  // Trivial, purely-local UI state (a single checkbox with no data
  // fetching or business logic attached) — kept inline rather than a
  // dedicated hooks file, same call as DropZone's `drag` boolean in
  // UploadFlowPage.
  const [checked, setChecked] = useState(false)
  // Educators can't truthfully affirm "I am this child's parent" — per the
  // ToS (§4.3) the launch consent pathway for educator-created learners is
  // that the educator has OBTAINED the parent/guardian's consent, so the
  // affirmation must say that in EDUCATOR mode.
  const mode = useGlobalState((s) => s.mode)

  return (
    <div className="create-learner-modal__consent-wrap">
      <div className="create-learner-modal__consent-card">
        <div className="create-learner-modal__consent-eyebrow">Before we begin</div>
        <h1 className="create-learner-modal__consent-heading">One quick thing first.</h1>
        <p className="create-learner-modal__consent-copy">
          Hopscotch collects a little information about your reader — their name and age — to
          personalize their practice. Before we do, we need to hear this from you.
        </p>

        <label className="create-learner-modal__consent-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="create-learner-modal__consent-checkbox"
            aria-label="Parental consent affirmation"
          />
          <span className="create-learner-modal__consent-text">
            {mode === 'EDUCATOR'
              ? <>I have obtained consent from this child&rsquo;s parent or legal guardian for Hopscotch to collect and use this information, and I agree to the{' '}</>
              : <>I am this child&rsquo;s parent or legal guardian, and I agree to the{' '}</>}
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="create-learner-modal__consent-link">
              Terms
            </a>{' '}
            &amp;{' '}
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="create-learner-modal__consent-link">
              Privacy Policy
            </a>
            .
          </span>
        </label>

        <button
          type="button"
          disabled={!checked}
          onClick={onConsent}
          className="hs-press create-learner-modal__consent-btn"
        >
          Continue
        </button>
      </div>
    </div>
  )
}

interface CreateLearnerModalProps {
  isOpen: boolean
  onClose: () => void
}

// Full-screen takeover mount — "Add a learner" from the learner switcher.
// Keeps the original contract ({ isOpen, onClose }) for existing callers.
export function CreateLearnerModal({ isOpen, onClose }: CreateLearnerModalProps) {
  const { canExit, consented, setConsented } = useCreateLearnerModal()
  const isMobile = useIsMobile()

  if (!isOpen) return null

  return createPortal(
    <div className="hs-brand create-learner-modal__overlay">
      <Grain />

      {/* top bar — logo left, quiet exit right. Shortened to "Exit" on
          phone: "Save & exit" reads as two actions on a narrow bar. */}
      <div className="create-learner-modal__topbar">
        <div className="create-learner-modal__brand">
          <span className="create-learner-modal__brand-mark" />
          <span className="create-learner-modal__brand-name">Hopscotch</span>
        </div>
        {canExit && (
          <button type="button" onClick={onClose} className="create-learner-modal__exit-btn">
            {isMobile ? 'Exit' : 'Save & exit'}
          </button>
        )}
      </div>

      {!canExit && <FirstRunBanner />}
      {consented ? (
        <ReaderCardSpread onCreated={onClose} />
      ) : (
        <ParentalConsentGate onConsent={() => setConsented(true)} />
      )}
    </div>,
    document.body,
  )
}
